/**
 * IntegrationPage (FND-009, FND-013).
 *
 * Operational view of the single integration engine: the connector job log
 * (GET /integration/jobs) and the dead-letter queue of permanently failed
 * operations (GET /integration/dead-letters). Read-only monitoring surface.
 * Data wiring (useIntegration) is unchanged.
 */
import { type CSSProperties } from 'react';
import { useIntegration } from '../hooks/useIntegration';
import type { DeadLetter, IntegrationJob } from '../api/types';
import { colors } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export function IntegrationPage() {
  const { jobs, deadLetters, isLoading, error, refresh } = useIntegration();

  const jobColumns: Column<IntegrationJob>[] = [
    { key: 'connector', header: 'Connector', render: (j) => <span style={{ fontWeight: 600 }}>{j.connector}</span> },
    { key: 'direction', header: 'Direction', render: (j) => <span style={muted}>{j.direction}</span> },
    { key: 'status', header: 'Status', render: (j) => <StatusBadge value={j.status} /> },
    { key: 'attempts', header: 'Attempts', align: 'right', render: (j) => j.attempts },
    { key: 'updated', header: 'Updated', render: (j) => <span style={muted}>{fmtDateTime(j.updated_at)}</span> },
    { key: 'error', header: 'Error', render: (j) => j.error ?? <span style={muted}>—</span> },
  ];

  const dlColumns: Column<DeadLetter>[] = [
    { key: 'connector', header: 'Connector', render: (d) => <span style={{ fontWeight: 600 }}>{d.connector}</span> },
    { key: 'direction', header: 'Direction', render: (d) => <span style={muted}>{d.direction}</span> },
    { key: 'attempts', header: 'Attempts', align: 'right', render: (d) => d.attempts },
    { key: 'error', header: 'Error', render: (d) => d.error },
    { key: 'last', header: 'Last attempt', render: (d) => <span style={muted}>{fmtDateTime(d.last_attempt_at)}</span> },
    {
      key: 'alerted',
      header: 'Alerted',
      render: (d) => <StatusBadge status={d.alerted ? 'success' : 'neutral'} label={d.alerted ? 'Yes' : 'No'} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="Integration Engine"
        subtitle="Connector job log and dead-letter queue for the single integration engine (FND-009, FND-013)."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      <Card title="Jobs" padded={false} style={{ marginBottom: 20 }}>
        {isLoading && (
          <div style={{ padding: 18 }}>
            <LoadingIndicator text="Loading integration jobs…" />
          </div>
        )}
        {error && (
          <p style={errorText} role="alert">
            {error.message}
          </p>
        )}
        {!isLoading && !error && (
          <DataTable columns={jobColumns} rows={jobs} rowKey={(j) => j.id} emptyTitle="No integration jobs recorded" />
        )}
      </Card>

      <Card title="Dead letters" padded={false}>
        {!isLoading && !error && (
          <DataTable
            columns={dlColumns}
            rows={deadLetters}
            rowKey={(d) => d.id}
            emptyTitle="No dead-lettered operations"
            emptyDescription="Permanently failed connector operations would appear here."
          />
        )}
      </Card>
    </>
  );
}

const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', padding: 18 };
